import { useMemo } from 'react';

import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryDefinition,
} from '@reduxjs/toolkit/dist/query';
import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type { Pagination } from 'models/pagination';
import { Role, Status, User } from 'models/user';
import type { FetchUsers } from 'services/rtk/users/users';
import { getCustomSortOrderName } from 'utils/helpers/getCustomSortOrderName';

import type { FiltersState } from './filters';

export type WTFUseUsersQuery = UseQuery<
  QueryDefinition<
    FetchUsers,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>,
    never,
    { data: User[]; pagination: Pagination },
    'api'
  >
>;

export type WTFUseTeamUsersQuery = UseQuery<
  QueryDefinition<
    FetchUsers & { id: number },
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>,
    never,
    { data: User[]; pagination: Pagination },
    'api'
  >
>;

interface Props {
  query: WTFUseUsersQuery | WTFUseTeamUsersQuery;
  otherParams?: Record<string, any>;
  page?: number;
  perPage?: number;
  filters: FiltersState;
  userRoleFilterValues: Role[];
  showIsActiveColumn?: boolean;
}

export const useQueryFetchData = ({
  otherParams,
  query,
  filters,
  page,
  perPage,
  userRoleFilterValues,
  showIsActiveColumn,
}: Props) => {
  const queryParams = {
    search: filters.searchStringQuery ? filters.searchStringQuery : undefined,
    roles: filters.filterInfo.role ? (filters.filterInfo.role as Role[]) : userRoleFilterValues,
    page: page ? page : undefined,
    limit: perPage ? perPage : undefined,
    sort: filters.sort.order ? getCustomSortOrderName(filters.sort.order) : undefined,
    orderBy: filters.sort.field && filters.sort.order ? (filters.sort.field as string) : 'id',
    statuses: showIsActiveColumn ? (filters.filterInfo.isActive as Status[] | undefined) : undefined,
    ...otherParams,
  };

  const { data: users, isFetching } = query(queryParams as any); //!

  const dataSource = useMemo(() => {
    return users?.data?.map((u) => {
      const { id, teams, ...rest } = u;

      const teamLead =
        !['ADMIN', 'PARTNER', 'HEAD'].includes(u.role) && teams
          ? teams[0].teamLeads?.map((tl) => tl.name).join(', ')
          : undefined;

      return { key: id, teamLead, ...rest };
    });
  }, [users]);

  return { data: dataSource, isFetching, pagination: users?.pagination };
};
